/*
 * R159ss · Feb 2026 — Project team management popup.
 *
 * Compact modal that lists every active member of a project and lets
 * an admin add / remove teammates without leaving the Planning Board.
 * Reuses the same server endpoints as the Project → Members tab:
 *   • GET    /projects/{pid}/members
 *   • POST   /projects/{pid}/members  { user_id }
 *   • DELETE /projects/{pid}/members/{uid}
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users, Trash2, UserPlus, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

export default function ProjectTeamDialog({ open, onClose, project, workspaceMembers = [], onChanged }) {
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!project?.id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/members`);
      setMembers(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load team"));
    } finally { setBusy(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, project?.id]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);

  const candidates = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (workspaceMembers || [])
      .map((m) => ({
        user_id: m.user_id || m.id,
        name: m.user?.name || m.name || m.email || "Unknown",
        email: m.user?.email || m.email || "",
      }))
      .filter((c) => c.user_id && !memberIds.has(c.user_id))
      .filter((c) => !query || c.name.toLowerCase().includes(query) || c.email.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 20);
  }, [workspaceMembers, memberIds, q]);

  const addMember = async (uid) => {
    if (!uid) return;
    setAdding(true);
    try {
      await api.post(`/projects/${project.id}/members`, { user_id: uid });
      await load();
      onChanged && onChanged();
      toast.success("Added to project");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add member"));
    } finally { setAdding(false); }
  };

  const removeMember = async (uid, name) => {
    if (!confirm(`Remove ${name || "this member"} from the project?`)) return;
    try {
      await api.delete(`/projects/${project.id}/members/${uid}`);
      await load();
      onChanged && onChanged();
      toast.success("Removed from project");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't remove"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="project-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Users className="w-4 h-4 text-primary" /> Team &middot; {project?.name}
          </DialogTitle>
          <DialogDescription>
            Add or remove teammates who can see and work on this project. Owners and
            the project manager are always included.
          </DialogDescription>
        </DialogHeader>

        {/* Current members */}
        <div className="border border-border" data-testid="project-team-current">
          <div className="px-2 py-1.5 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-2">
            <span>Current members</span>
            <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">· {members.length}</span>
            {busy && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
          </div>
          <ul className="max-h-52 overflow-y-auto divide-y divide-border">
            {members.length === 0 && !busy && (
              <li className="px-3 py-3 text-xs text-muted-foreground italic">No members yet — add one below.</li>
            )}
            {members.map((m) => {
              const name = m.user?.name || m.name || m.email || m.user_id?.slice(0, 6);
              const email = m.user?.email || m.email || "";
              const isOwner = project?.owner_id === m.user_id;
              const isPM = (project?.project_manager_id || project?.pm_id) === m.user_id;
              return (
                <li key={m.user_id} className="flex items-center gap-2 px-2 py-1.5"
                    data-testid={`project-team-row-${m.user_id}`}>
                  <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-semibold shrink-0">
                    {(name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{name}</div>
                    {email && <div className="text-[10px] text-muted-foreground truncate">{email}</div>}
                  </div>
                  {isOwner && <span className="text-[9px] uppercase tracking-widest font-semibold text-primary shrink-0">Owner</span>}
                  {isPM && !isOwner && <span className="text-[9px] uppercase tracking-widest font-semibold text-primary shrink-0">PM</span>}
                  {!isOwner && !isPM && (
                    <button type="button"
                            onClick={() => removeMember(m.user_id, name)}
                            className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                            title="Remove from project"
                            data-testid={`project-team-remove-${m.user_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Add member picker */}
        <div className="border border-border" data-testid="project-team-add">
          <div className="px-2 py-1.5 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            Add teammate
          </div>
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a workspace member…"
                className="w-full pl-7 pr-2 h-7 text-xs border border-input outline-none focus:border-primary"
                data-testid="project-team-add-search"
              />
            </div>
          </div>
          <ul className="max-h-40 overflow-y-auto divide-y divide-border">
            {candidates.length === 0 && (
              <li className="px-3 py-3 text-xs text-muted-foreground italic">
                {q ? "No workspace members match." : "Everyone in the workspace is already on this project."}
              </li>
            )}
            {candidates.map((c) => (
              <li key={c.user_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50"
                  data-testid={`project-team-candidate-${c.user_id}`}>
                <span className="w-6 h-6 rounded-full bg-muted text-foreground flex items-center justify-center text-[10px] font-semibold shrink-0">
                  {(c.name || "?").slice(0, 2).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{c.name}</div>
                  {c.email && <div className="text-[10px] text-muted-foreground truncate">{c.email}</div>}
                </div>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] rounded-none"
                        onClick={() => addMember(c.user_id)}
                        disabled={adding}
                        data-testid={`project-team-add-${c.user_id}`}>
                  <UserPlus className="w-3 h-3 mr-1" /> Add
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
